import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, ShieldX, Award, Building2, GraduationCap,
  Calendar, User, BookOpen, Loader2, AlertTriangle, ArrowLeft,
} from 'lucide-react';

import { API } from '@/config';

const CertificateVerifyPage = () => {
  const { certNumber } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (certNumber) verify();
  }, [certNumber]);

  const verify = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/certificates/verify/${certNumber}`);
      setResult(res.data);
    } catch {
      setResult({ is_valid: false, message: 'Verification service unavailable. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#20acf6] mx-auto mb-4" />
          <p className="text-slate-600">Verifying certificate...</p>
        </div>
      </div>
    );
  }

  const isValid = result?.is_valid;
  const completionDate = result?.completion_date ? new Date(result.completion_date).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  }) : '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] to-white" data-testid="certificate-verify-page">
      {/* Header */}
      <header className="bg-[#2a3e5d] text-white py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Award className="h-6 w-6 text-[#20acf6]" />
            <span className="font-semibold text-lg">Common Impact E-learning</span>
          </div>
          <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10" asChild>
            <Link to="/"><ArrowLeft className="h-4 w-4 mr-1" /> Home</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2a3e5d]">Certificate Verification</h1>
          <p className="text-slate-500 mt-2">Independent verification of training completion</p>
        </div>

        {/* Status Banner */}
        <Card className={`mb-6 border-2 ${isValid ? 'border-green-400 bg-green-50/50' : 'border-red-400 bg-red-50/50'}`} data-testid="verify-status-card">
          <CardContent className="p-6 flex items-center gap-4">
            {isValid ? (
              <ShieldCheck className="h-12 w-12 text-green-600 shrink-0" />
            ) : (
              <ShieldX className="h-12 w-12 text-red-600 shrink-0" />
            )}
            <div>
              <Badge
                className={`text-sm px-3 py-1 ${isValid ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                data-testid="verify-status-badge"
              >
                {isValid ? 'VALID CERTIFICATE' : 'INVALID / REVOKED'}
              </Badge>
              <p className="text-sm text-slate-600 mt-2" data-testid="verify-message">{result?.message}</p>
            </div>
          </CardContent>
        </Card>

        {/* Certificate Details */}
        {(result?.user_name || result?.course_title) && (
          <Card className="border-slate-200" data-testid="verify-details-card">
            <CardContent className="p-6 space-y-5">
              <h2 className="text-lg font-semibold text-[#2a3e5d] border-b border-slate-200 pb-3">Certificate Details</h2>

              <div className="grid gap-4">
                <DetailRow icon={User} label="Certificate Holder" value={result.user_name} testId="verify-holder" />
                <DetailRow icon={BookOpen} label="Course" value={result.course_title} testId="verify-course" />
                {completionDate && (
                  <DetailRow icon={Calendar} label="Issued" value={completionDate} testId="verify-date" />
                )}
                <DetailRow icon={ShieldCheck} label="Certificate Number" value={certNumber} testId="verify-cert-number" mono />
                {result.organisation_name && (
                  <DetailRow icon={Building2} label="Organisation" value={result.organisation_name} testId="verify-org" />
                )}
                {result.is_accredited && (
                  <DetailRow
                    icon={GraduationCap}
                    label="Accreditation"
                    value={result.accreditation_body || 'Accredited Course'}
                    testId="verify-accreditation"
                    badge
                  />
                )}
              </div>

              {!isValid && result.revoke_reason && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="verify-revoke-reason">
                  <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Revocation Reason</p>
                    <p className="text-sm text-red-600">{result.revoke_reason}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-400">
                  This certificate is a permanent record issued by Common Impact E-learning.
                  It does not expire and can be independently verified at any time using this page.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value, testId, mono, badge }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <Icon className="h-5 w-5 text-[#20acf6] shrink-0 mt-0.5" />
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      {badge ? (
        <Badge className="bg-[#02d1d4] text-[#2a3e5d] mt-0.5">{value}</Badge>
      ) : (
        <p className={`text-sm font-medium text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</p>
      )}
    </div>
  </div>
);

export default CertificateVerifyPage;
